import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, CircularProgress, Drawer, IconButton, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { colorAdjustedProductType } from "../../../utils/colorEnumFormatter";
import { formatHandlerCode, formatCurrency } from "../../../utils/namesFormater";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { AdjustedProductType } from "../../../interfaces/DTO/mislaka_xml_models";

interface TransferFlowDiagramProps {
  data: MimshakData[];
  loading?: boolean;
}

interface TransferNode {
  id: string;
  label: string;
  handlerCode: string;
  productType: AdjustedProductType;
  color: string;
}

interface TransferEdge {
  from: string;
  to: string;
  amount: number;
  date: string;
  policyNumber: string;
  productType: AdjustedProductType;
  color: string;
  isMatched?: boolean; // האם נמצא match לניוד הזה
  savingFeeFrom: number; // דמי ניהול מהקופה היוצאת
  savingFeeTo: number; // דמי ניהול מהקופה המקבלת
}

interface TransferMatch {
  targetNodeId: string;
  isMatched: boolean;
  matchedPolicyNumber?: string; // מספר הפוליסה המקבלת שנמצאה
}

interface MatchPanelData {
  nodeId: string;
  handlerCode: string;
  productType: AdjustedProductType;
  isMatched: boolean;
  outgoingTransfers: MimshakData[];
  incomingTransfers: MimshakData[];
  matchedPolicyNumber?: string;
}

const TransferFlowDiagram: React.FC<TransferFlowDiagramProps> = ({ data, loading }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelData, setPanelData] = useState<MatchPanelData | null>(null);

  // פונקציה לפתיחת הפאנל
  const handleMatchClick = (nodeId: string, handlerCode: string, productType: AdjustedProductType) => {
    // מציאת כל ההעברות הרלוונטיות
    const outgoing = data.filter(
      (record) =>
        record.handler_code === handlerCode &&
        (record.adjusted_product_type ?? record.product_type) === productType &&
        record.transfer_out != null &&
        record.transfer_out !== 0 &&
        record.transfer_out_to
    );

    const incoming = data.filter(
      (record) =>
        record.handler_code === handlerCode &&
        (record.adjusted_product_type ?? record.product_type) === productType &&
        record.transfer_in != null &&
        record.transfer_in !== 0
    );

    setPanelData({
      nodeId,
      handlerCode,
      productType,
      isMatched: matchedNodes.get(nodeId) || false,
      outgoingTransfers: outgoing,
      incomingTransfers: incoming,
      matchedPolicyNumber: matchedPolicies.get(nodeId),
    });
    setPanelOpen(true);
  };

  const { nodes, edges, matchedNodes, matchedPolicies } = useMemo(() => {
    const nodesMap = new Map<string, TransferNode>();
    const edgesMap = new Map<string, TransferEdge>(); // key: from-to, value: aggregated edge
    const matchResults = new Map<string, boolean>(); // nodeId -> isMatched
    const matchedPolicyNumbers = new Map<string, string>(); // nodeId -> matched policy number
    const policyNumbersByEdge = new Map<string, Set<string>>(); // key: from-to, value: policy numbers
    const savingFeesByEdge = new Map<string, { from: Set<number>; to: Set<number> }>(); // key: from-to, value: unique saving fees

    // שלב 1: איסוף כל ההעברות היוצאות והנכנסות
    const transfersOut = new Map<string, MimshakData[]>(); // handler_code -> records
    const transfersIn = new Map<string, MimshakData[]>();  // handler_code -> records

    data.forEach((record) => {
      const handlerCode = record.handler_code;

      if (record.transfer_out != null && record.transfer_out !== 0 && record.transfer_out_to) {
        if (!transfersOut.has(handlerCode)) {
          transfersOut.set(handlerCode, []);
        }
        transfersOut.get(handlerCode)!.push(record);
      }

      if (record.transfer_in != null && record.transfer_in !== 0) {
        if (!transfersIn.has(handlerCode)) {
          transfersIn.set(handlerCode, []);
        }
        transfersIn.get(handlerCode)!.push(record);
      }
    });

    // שלב 2: בניית הגרף ובדיקת matches
    data.forEach((record) => {
      const handlerCode = record.handler_code || "unknown";
      // אם אין adjusted_product_type, נשתמש ב-product_type הרגיל
      const productType = (record.adjusted_product_type ?? record.product_type ?? 0) as AdjustedProductType;
      const policyNumber = record.policy_number || "";
      const transferOut = record.transfer_out;
      const transferOutTo = record.transfer_out_to;
      const transferIn = record.transfer_in;
      const reportedPeriod = record.reported_period || "";

      const nodeId = `${handlerCode}-${productType}`;
      const color = colorAdjustedProductType(productType);

      // יצירת node עבור הקופה המקורית
      if (!nodesMap.has(nodeId)) {
        nodesMap.set(nodeId, {
          id: nodeId,
          label: formatHandlerCode(handlerCode),
          handlerCode,
          productType,
          color,
        });
      }

      // טיפול ב-transfer_out (תומך גם בערכים שליליים)
      if (transferOut != null && transferOut !== 0 && transferOutTo) {
        const absoluteAmount = Math.abs(transferOut);

        const targetNodeId = `${transferOutTo}-${productType}`;
        const targetColor = colorAdjustedProductType(productType);

        if (!nodesMap.has(targetNodeId)) {
          nodesMap.set(targetNodeId, {
            id: targetNodeId,
            label: formatHandlerCode(transferOutTo),
            handlerCode: transferOutTo,
            productType,
            color: targetColor,
          });
        }

        // בדיקת match: האם קיימת העברה נכנסת תואמת?
        const matchResult = checkTransferMatch(
          transferOutTo,
          absoluteAmount,
          reportedPeriod,
          record.join_date,
          transfersIn
        );

        // שמירת תוצאת ה-match עבור ה-node המקבל
        if (!matchResults.has(targetNodeId) || matchResult.isMatched) {
          matchResults.set(targetNodeId, matchResult.isMatched);
          if (matchResult.matchedPolicyNumber) {
            matchedPolicyNumbers.set(targetNodeId, matchResult.matchedPolicyNumber);
          }
        }

        // צירוף edges - סכום סכומים עבור אותו מקור-יעד
        const edgeKey = `${nodeId}->${targetNodeId}`;

        if (!policyNumbersByEdge.has(edgeKey)) {
          policyNumbersByEdge.set(edgeKey, new Set());
        }
        policyNumbersByEdge.get(edgeKey)!.add(policyNumber);

        // צבירת דמי ניהול - שמירת ערכים ייחודיים
        const currentSavingFee = record.saving_fee || 0;
        if (!savingFeesByEdge.has(edgeKey)) {
          savingFeesByEdge.set(edgeKey, {
            from: new Set<number>(),
            to: new Set<number>()
          });
        }
        const fees = savingFeesByEdge.get(edgeKey)!;
        if (currentSavingFee > 0) {
          fees.from.add(currentSavingFee); // דמי ניהול מהקופה היוצאת
        }

        // מציאת דמי ניהול מהקופה המקבלת
        const targetTransfers = transfersIn.get(transferOutTo);
        if (targetTransfers) {
          targetTransfers.forEach(t => {
            if (t.reported_period === reportedPeriod && t.saving_fee && t.saving_fee > 0) {
              fees.to.add(t.saving_fee);
            }
          });
        }

        if (edgesMap.has(edgeKey)) {
          // כבר קיים edge - נצרף את הסכום
          const existingEdge = edgesMap.get(edgeKey)!;
          existingEdge.amount += absoluteAmount;
          // עדכון isMatched - אם לפחות אחד matched
          existingEdge.isMatched = existingEdge.isMatched || matchResult.isMatched;
        } else {
          // edge חדש
          edgesMap.set(edgeKey, {
            from: nodeId,
            to: targetNodeId,
            amount: absoluteAmount,
            date: reportedPeriod,
            policyNumber, // נעדכן מאוחר יותר לכל המספרים
            productType,
            color: "#333", // קווים שחורים
            isMatched: matchResult.isMatched,
            savingFeeFrom: 0, // יעודכן בהמשך
            savingFeeTo: 0, // יעודכן בהמשך
          });
        }
      }
    });

    // פונקציה לבדיקת match
    function checkTransferMatch(
      targetHandlerCode: string,
      amount: number,
      reportedPeriod: string,
      joinDate: string | null | undefined,
      transfersInMap: Map<string, MimshakData[]>
    ): { isMatched: boolean; matchedPolicyNumber?: string } {
      const incomingTransfers = transfersInMap.get(targetHandlerCode);
      if (!incomingTransfers || incomingTransfers.length === 0) {
        return { isMatched: false };
      }

      // חיפוש העברה נכנסת תואמת
      const matchedRecord = incomingTransfers.find((inRecord) => {
        const inAmount = Math.abs(inRecord.transfer_in || 0);
        const amountMatch = Math.abs(amount - inAmount) < 100; // טולרנס של 100 שקלים
        const periodMatch = inRecord.reported_period === reportedPeriod;
        const dateMatch = !joinDate || inRecord.join_date === joinDate;

        return amountMatch && periodMatch && dateMatch;
      });

      if (matchedRecord) {
        return {
          isMatched: true,
          matchedPolicyNumber: matchedRecord.policy_number || undefined
        };
      }

      return { isMatched: false };
    }

    // עדכון מספרי הפוליסות ודמי הניהול לכל edge
    edgesMap.forEach((edge, edgeKey) => {
      const policyNumbers = policyNumbersByEdge.get(edgeKey);
      if (policyNumbers && policyNumbers.size > 0) {
        edge.policyNumber = Array.from(policyNumbers).join(", ");
      }

      // עדכון דמי ניהול - חישוב ממוצע של ערכים ייחודיים
      const fees = savingFeesByEdge.get(edgeKey);
      if (fees) {
        const fromArray = Array.from(fees.from);
        const toArray = Array.from(fees.to);

        edge.savingFeeFrom = fromArray.length > 0
          ? fromArray.reduce((sum, val) => sum + val, 0) / fromArray.length
          : 0;

        edge.savingFeeTo = toArray.length > 0
          ? toArray.reduce((sum, val) => sum + val, 0) / toArray.length
          : 0;
      }
    });

    return {
      nodes: Array.from(nodesMap.values()),
      edges: Array.from(edgesMap.values()),
      matchedNodes: matchResults,
      matchedPolicies: matchedPolicyNumbers,
    };
  }, [data]);

  // חישוב מיקומים עבור ה-nodes - נפריד בין מקור ליעד וממיינים לפי productType
  const nodePositions = useMemo(() => {
    const positions = new Map<string, { x: number; y: number }>();
    const spacing = 120;

    // מציאת כל ה-nodes שהם מקור (יש להם edges יוצאים)
    const sourceNodes = new Set(edges.map((e: TransferEdge) => e.from));
    const targetNodes = new Set(edges.map((e: TransferEdge) => e.to));

    // פונקציה למיון לפי productType
    const sortByProductType = (a: TransferNode, b: TransferNode) => a.productType - b.productType;

    // nodes שהם רק מקור (ממוינים)
    const onlySource = nodes
      .filter(n => sourceNodes.has(n.id) && !targetNodes.has(n.id))
      .sort(sortByProductType);

    // nodes שהם גם מקור וגם יעד (ממוינים)
    const both = nodes
      .filter(n => sourceNodes.has(n.id) && targetNodes.has(n.id))
      .sort(sortByProductType);

    // nodes שהם רק יעד (ממוינים)
    const onlyTarget = nodes
      .filter(n => !sourceNodes.has(n.id) && targetNodes.has(n.id))
      .sort(sortByProductType);

    let currentY = 50;

    // מקור - בצד שמאל (כולל nodes שהם גם מקור וגם יעד)
    [...onlySource, ...both].forEach((node) => {
      positions.set(node.id, {
        x: 100,
        y: currentY,
      });
      currentY += spacing;
    });

    // יעד - בצד ימין
    currentY = 50;
    // nodes שהם רק יעד מופיעים בימין
    onlyTarget.forEach((node) => {
      positions.set(node.id, {
        x: 600,
        y: currentY,
      });
      currentY += spacing;
    });

    // nodes שהם גם מקור וגם יעד - מופיעים גם בימין באותו Y כמו בשמאל
    both.forEach((node) => {
      const leftPos = positions.get(node.id);
      if (leftPos) {
        // נשתמש ב-ID מיוחד כדי לייצג את אותו node בצד ימין
        positions.set(`${node.id}-target`, {
          x: 600,
          y: leftPos.y, // אותו גובה כמו בשמאל
        });
      }
    });

    return positions;
  }, [nodes, edges]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (edges.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          לא נמצאו ניודים בתקופה הנבחרת
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, overflow: "auto" }}>
      <Typography variant="h6" gutterBottom>
        ניודים בין קופות
      </Typography>

      {/* מקרא */}
      <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 2 }}>
        {Array.from(new Set(nodes.map((n) => n.productType))).map((pt) => (
          <Box key={pt} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: colorAdjustedProductType(pt),
                borderRadius: 1,
              }}
            />
            <Typography variant="caption">{formatAdjustedProductType(pt)}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ position: "relative", minHeight: 400, minWidth: 900 }}>
        <svg width="100%" height={Math.max(400, nodes.length * 120)} style={{ direction: "ltr" }}>
          {/* הגדרת חץ */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#666" />
            </marker>
          </defs>

          {/* ציור ה-nodes - קודם כדי שיהיו מאחורי החצים */}
          {nodes.flatMap((node) => {
            // מציאת כל המיקומים של ה-node (יכול להיות גם בשמאל וגם בימין)
            const sourceNodes = new Set(edges.map(e => e.from));
            const targetNodes = new Set(edges.map(e => e.to));
            const isBoth = sourceNodes.has(node.id) && targetNodes.has(node.id);

            const positions = [];
            const leftPos = nodePositions.get(node.id);
            if (leftPos) positions.push({ ...leftPos, role: 'source', key: node.id });

            // אם ה-node גם מעביר וגם מקבל, הוא צריך להופיע גם בצד ימין
            if (isBoth) {
              const rightPos = nodePositions.get(`${node.id}-target`);
              if (rightPos) positions.push({ ...rightPos, role: 'target', key: `${node.id}-target` });
            }

            return positions.map((pos) => {
              // מציאת פוליסות שיוצאות מה-node הזה (מעבירות)
              const outgoingPolicies = edges
                .filter(e => e.from === node.id)
                .map(e => e.policyNumber)
                .filter((v, i, a) => v && a.indexOf(v) === i) // unique
                .join(", ");

              // מציאת פוליסות שנכנסות ל-node הזה (מקבלות)
              // אם נמצאה התאמה, נציג את מספר הפוליסה המקבלת מה-match
              const matchedPolicyNumber = matchedPolicies.get(node.id);
              const incomingPolicies = matchedPolicyNumber || edges
                .filter(e => e.to === node.id)
                .map(e => e.policyNumber)
                .filter((v, i, a) => v && a.indexOf(v) === i) // unique
                .join(", ");

              // בדיקה אם ה-node הזה מקבל העברה ואם היא matched
              const isReceivingNode = edges.some(e => e.to === node.id);
              const matchStatus = matchedNodes.get(node.id);

              // אם זה העתק בצד ימין, נציג רק פוליסות מקבלות
              const showOutgoing = pos.role === 'source';
              const showIncoming = pos.role === 'target' || !isBoth;

              return (
                <g key={pos.key}>
                {/* מלבן עם צבע המוצר */}
                <rect
                  x={pos.x}
                  y={pos.y}
                  width="150"
                  height="40"
                  rx="8"
                  fill={node.color}
                  opacity="0.85"
                  stroke="#333"
                  strokeWidth="2"
                />

                {/* טקסט בתוך ה-node */}
                <text
                  x={pos.x + 75}
                  y={pos.y + 25}
                  textAnchor="middle"
                  fill="white"
                  fontSize="13"
                  fontWeight="bold"
                >
                  {node.label}
                </text>

                {/* סימון match/no-match מעל הקוביה (רק אם זה node מקבל וזה העותק בצד ימין) */}
                {showIncoming && isReceivingNode && matchStatus !== undefined && (
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleMatchClick(node.id, node.handlerCode, node.productType)}
                  >
                    {matchStatus ? (
                      // V ירוק - matched
                      <>
                        <title>
                          ✓ מאושר: נמצא דיווח תואם על העברה נכנסת{"\n"}
                          לחץ לפרטים נוספים
                        </title>
                        <circle
                          cx={pos.x + 140}
                          cy={pos.y - 10}
                          r="12"
                          fill="#4CAF50"
                          stroke="white"
                          strokeWidth="2"
                        />
                        <text
                          x={pos.x + 140}
                          y={pos.y - 6}
                          textAnchor="middle"
                          fill="white"
                          fontSize="16"
                          fontWeight="bold"
                          pointerEvents="none"
                        >
                          ✓
                        </text>
                      </>
                    ) : (
                      // X אדום - not matched
                      <>
                        <title>
                          ✗ לא מאושר: לא נמצא דיווח תואם על העברה נכנסת{"\n"}
                          לחץ לפרטים נוספים
                        </title>
                        <circle
                          cx={pos.x + 140}
                          cy={pos.y - 10}
                          r="12"
                          fill="#F44336"
                          stroke="white"
                          strokeWidth="2"
                        />
                        <text
                          x={pos.x + 140}
                          y={pos.y - 5}
                          textAnchor="middle"
                          fill="white"
                          fontSize="16"
                          fontWeight="bold"
                          pointerEvents="none"
                        >
                          ✗
                        </text>
                      </>
                    )}
                  </g>
                )}

                {/* מספר פוליסה מעבירה - מעל הקוביה */}
                {showOutgoing && outgoingPolicies && (
                  <text
                    x={pos.x + 75}
                    y={pos.y - 8}
                    textAnchor="middle"
                    fill="#666"
                    fontSize="10"
                  >
                    {outgoingPolicies}
                  </text>
                )}

                {/* מספר פוליסה מקבלת - מתחת לקוביה */}
                {showIncoming && incomingPolicies && (
                  <text
                    x={pos.x + 75}
                    y={pos.y + 55}
                    textAnchor="middle"
                    fill="#666"
                    fontSize="10"
                  >
                    {incomingPolicies}
                  </text>
                )}
              </g>
              );
            });
          })}

          {/* ציור החצים - אחרי ה-nodes כדי שיהיו מעליהם */}
          {edges.map((edge, index) => {
            const fromPos = nodePositions.get(edge.from);

            // בדיקה אם ה-target הוא גם source (יש לו גם edges יוצאים)
            const targetNodeId = edge.to;
            const targetIsAlsoSource = edges.some(e => e.from === targetNodeId);

            // אם ה-target גם מעביר, נתחבר לצד ימין שלו (-target)
            let toPos = targetIsAlsoSource
              ? nodePositions.get(`${targetNodeId}-target`)
              : nodePositions.get(targetNodeId);

            if (!fromPos || !toPos) return null;

            const startX = fromPos.x + 150;
            const startY = fromPos.y + 20;
            const endX = toPos.x;
            const endY = toPos.y + 20;
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;

            // חישוב bezier curve control points
            const controlX1 = startX + (endX - startX) * 0.4;
            const controlX2 = startX + (endX - startX) * 0.6;

            return (
              <g key={`edge-${index}`}>
                {/* קו מעוקל בסגנון Sankey */}
                <path
                  d={`M ${startX} ${startY}
                      C ${controlX1} ${startY}, ${controlX2} ${endY}, ${endX} ${endY}`}
                  stroke={edge.color}
                  strokeWidth="4"
                  fill="none"
                  opacity="0.5"
                  markerEnd="url(#arrowhead)"
                />

                {/* דמי ניהול - קופה יוצאת (בתחילת החץ, ליד הקוביה) */}
                {edge.savingFeeFrom > 0 && (
                  <text
                    x={startX + 15}
                    y={startY - 15}
                    textAnchor="start"
                    fill="#e65100"
                    fontSize="11"
                    fontWeight="bold"
                  >
                    {edge.savingFeeFrom.toFixed(2)}%
                  </text>
                )}

                {/* טקסט עם הסכום */}
                <text
                  x={midX}
                  y={midY - 10}
                  textAnchor="middle"
                  fill="#333"
                  fontSize="13"
                  fontWeight="bold"
                >
                  ₪{Math.round(edge.amount).toLocaleString("he-IL")}
                </text>

                {/* דמי ניהול - קופה מקבלת (בסוף החץ, ליד המשולש) */}
                {edge.savingFeeTo > 0 && (
                  <text
                    x={endX - 15}
                    y={endY - 15}
                    textAnchor="end"
                    fill="#333"
                    fontSize="11"
                    fontWeight="bold"
                  >
                    {edge.savingFeeTo.toFixed(2)}%
                  </text>
                )}

                {/* תאריך */}
                <text
                  x={midX}
                  y={midY + 12}
                  textAnchor="middle"
                  fill="#666"
                  fontSize="10"
                >
                  {new Date(edge.date).toLocaleDateString("he-IL", {
                    year: "numeric",
                    month: "2-digit"
                  })}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>

      {/* פאנל צדדי עם פרטי ההתאמה */}
      <Drawer
        anchor="right"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 500,
            maxWidth: '90vw',
            direction: 'rtl',
          },
        }}
      >
        {panelData && (
          <Box sx={{ p: 3, height: '100%', overflow: 'auto', direction: 'rtl' }}>
            {/* כותרת */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                פרטי ניודים - {formatHandlerCode(panelData.handlerCode)}
              </Typography>
              <IconButton onClick={() => setPanelOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* סטטוס התאמה */}
            <Box sx={{ mb: 3, p: 2, bgcolor: panelData.isMatched ? '#e8f5e9' : '#ffebee', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: panelData.isMatched ? '#2e7d32' : '#c62828' }}>
                {panelData.isMatched ? '✓ נמצאה התאמה' : '✗ לא נמצאה התאמה'}
              </Typography>
              {panelData.matchedPolicyNumber && (
                <Typography variant="body2" color="text.secondary">
                  פוליסה מקבלת: {panelData.matchedPolicyNumber}
                </Typography>
              )}
            </Box>

            {/* העברות יוצאות */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#d32f2f' }}>
                העברות יוצאות ({panelData.outgoingTransfers.length})
              </Typography>
              {panelData.outgoingTransfers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  אין העברות יוצאות
                </Typography>
              ) : (
                panelData.outgoingTransfers.map((transfer, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>פוליסה:</strong> {transfer.policy_number}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>יצרן:</strong> {formatHandlerCode(panelData.handlerCode)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                      סכום: {formatCurrency(Math.abs(transfer.transfer_out || 0))}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>יעד:</strong> {formatHandlerCode(transfer.transfer_out_to || '')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>תקופה:</strong> {transfer.reported_period}
                    </Typography>
                    {transfer.join_date && (
                      <Typography variant="body2">
                        <strong>תאריך הצטרפות:</strong> {transfer.join_date}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* העברות נכנסות */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#388e3c' }}>
                העברות נכנסות ({panelData.incomingTransfers.length})
              </Typography>
              {panelData.incomingTransfers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  אין העברות נכנסות
                </Typography>
              ) : (
                panelData.incomingTransfers.map((transfer, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #81c784' }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>פוליסה:</strong> {transfer.policy_number}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>יצרן:</strong> {formatHandlerCode(panelData.handlerCode)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                      סכום: {formatCurrency(Math.abs(transfer.transfer_in || 0))}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>תקופה:</strong> {transfer.reported_period}
                    </Typography>
                    {transfer.join_date && (
                      <Typography variant="body2">
                        <strong>תאריך הצטרפות:</strong> {transfer.join_date}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Paper>
  );
};

export default TransferFlowDiagram;
